import { useCallback } from 'react';
import { MIN_SLOT_MINUTES, TIMELINE_END_MINUTES, TIMELINE_START_MINUTES } from '../constants';
import {
  createFocusTimeSlotFromMinutes,
  createSelectableDefaultSlot,
  getAvailableSlot,
  isSelectableSlot,
  isSelectableSlotEnd,
  updateFocusTimeSlotMinutes,
} from '../helpers/helpers';
import {
  clampToRange,
  createSlotId,
  getMinCreatableMinutes,
  getMinSelectableSlotEndMinutes,
  getSlotEndMinutes,
  getSlotStartMinutes,
  overlapsAny,
  roundToStep,
} from '../helpers/time';
import { FocusTimeHandle, FocusTimeSlot } from '../types';

type UseFocusTimeSlotActionsParams = Readonly<{
  commitSlots: (producer: (previousSlots: FocusTimeSlot[]) => FocusTimeSlot[]) => void;
}>;

export const useFocusTimeSlotActions = ({ commitSlots }: UseFocusTimeSlotActionsParams) => {
  const appendDefaultSlot = useCallback(() => {
    const minCreatableMinutes = getMinCreatableMinutes();

    commitSlots(previousSlots => {
      const defaultSlot = createSelectableDefaultSlot(minCreatableMinutes, previousSlots);
      if (!defaultSlot || overlapsAny(defaultSlot, previousSlots)) {
        return previousSlots;
      }

      return [...previousSlots, defaultSlot];
    });
  }, [commitSlots]);

  const addSlotAt = useCallback(
    (minutes: number) => {
      const minCreatableMinutes = getMinCreatableMinutes();
      const requestedStartMinutes = clampToRange(roundToStep(minutes), TIMELINE_START_MINUTES, TIMELINE_END_MINUTES);
      if (requestedStartMinutes < minCreatableMinutes) {
        return;
      }

      commitSlots(previousSlots => {
        const availableSlot = getAvailableSlot(requestedStartMinutes, previousSlots);
        if (!availableSlot) {
          return previousSlots;
        }

        const nextSlot = createFocusTimeSlotFromMinutes(
          createSlotId(),
          availableSlot.startMinutes,
          availableSlot.endMinutes,
        );
        if (!isSelectableSlot(nextSlot, minCreatableMinutes) || overlapsAny(nextSlot, previousSlots)) {
          return previousSlots;
        }

        return [...previousSlots, nextSlot];
      });
    },
    [commitSlots],
  );

  const updateSlot = useCallback(
    (slotId: string, handle: FocusTimeHandle, minutes: number) => {
      commitSlots(previousSlots => {
        const previousSlot = previousSlots.find(slot => slot.id === slotId);
        if (!previousSlot) {
          return previousSlots;
        }

        const minCreatableMinutes = getMinCreatableMinutes();
        const minSelectableSlotEndMinutes = getMinSelectableSlotEndMinutes();
        const previousStartMinutes = getSlotStartMinutes(previousSlot);
        const previousEndMinutes = getSlotEndMinutes(previousSlot);
        const nextSlot =
          handle === FocusTimeHandle.Start
            ? updateFocusTimeSlotMinutes(
                previousSlot,
                clampToRange(roundToStep(minutes), minCreatableMinutes, previousEndMinutes - MIN_SLOT_MINUTES),
                previousEndMinutes,
              )
            : updateFocusTimeSlotMinutes(
                previousSlot,
                previousStartMinutes,
                clampToRange(
                  roundToStep(minutes),
                  Math.max(previousStartMinutes + MIN_SLOT_MINUTES, minSelectableSlotEndMinutes),
                  TIMELINE_END_MINUTES,
                ),
              );

        const isSelectable =
          handle === FocusTimeHandle.Start
            ? isSelectableSlot(nextSlot, minCreatableMinutes)
            : isSelectableSlotEnd(nextSlot, minSelectableSlotEndMinutes);

        if (!isSelectable || overlapsAny(nextSlot, previousSlots)) {
          return previousSlots;
        }

        return previousSlots.map(slot => (slot.id === slotId ? nextSlot : slot));
      });
    },
    [commitSlots],
  );

  const removeSlot = useCallback(
    (slotId: string) => {
      commitSlots(previousSlots => previousSlots.filter(slot => slot.id !== slotId));
    },
    [commitSlots],
  );

  return {
    appendDefaultSlot,
    addSlotAt,
    updateSlot,
    removeSlot,
  };
};
